import { Stack } from "@mui/material";
import { useAtom } from "jotai";
import type { FC } from "react";
import { useDocumentsFeatureOptional } from "web_myplann/Api/Rights/RightsProvider";
import {
	TimelineHorizonEnum,
	UpcomingTimelineItemTypeFilterEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { timelineFilterAtom } from "web_myplann/NotificationCenter/Atoms/timelineFilterAtom";
import { DocumentsFilterRow } from "web_myplann/NotificationCenter/FilterScreen/DocumentsFilterRow";
import { FilterRow } from "web_myplann/NotificationCenter/FilterScreen/FilterRow";
import { FilterSection } from "web_myplann/NotificationCenter/FilterScreen/FilterSection";
import {
	horizonResource,
	typeResource,
} from "web_myplann/NotificationCenter/Utils/filterLabels";
import { isDocumentsOnlyActive } from "web_myplann/NotificationCenter/Utils/isDocumentsOnlyActive";

const HORIZON_OPTIONS: TimelineHorizonEnum[] = [
	TimelineHorizonEnum.Month,
	TimelineHorizonEnum.HalfYear,
	TimelineHorizonEnum.Year,
];

const TYPE_OPTIONS: UpcomingTimelineItemTypeFilterEnum[] = [
	UpcomingTimelineItemTypeFilterEnum.All,
	UpcomingTimelineItemTypeFilterEnum.Activities,
	UpcomingTimelineItemTypeFilterEnum.Events,
];

export const UpcomingFilters: FC = () => {
	const { t } = useResource();
	const [filter, setFilter] = useAtom(timelineFilterAtom);
	const hasDocumentsFeature = useDocumentsFeatureOptional();
	const documentsOnly = isDocumentsOnlyActive(
		filter.upcomingDocumentsOnly,
		hasDocumentsFeature,
	);

	return (
		<Stack data-testid="notification-center-filter-upcoming">
			<FilterSection
				title={t(Resources.NotificationCenter.FilterScreen.HorizonSectionTitle)}
				testIdPrefix="horizon"
			>
				{HORIZON_OPTIONS.map((option, i) => (
					<FilterRow
						key={option}
						label={t(horizonResource(option))}
						selected={filter.horizon === option}
						onClick={() => setFilter((prev) => ({ ...prev, horizon: option }))}
						isLast={i === HORIZON_OPTIONS.length - 1}
						testId={`notification-center-filter-horizon-${option}`}
					/>
				))}
			</FilterSection>
			<FilterSection
				title={t(Resources.NotificationCenter.FilterScreen.TypeSectionTitle)}
				testIdPrefix="type"
			>
				{TYPE_OPTIONS.map((option, i) => (
					<FilterRow
						key={option}
						label={t(typeResource(option))}
						selected={filter.upcomingType === option && !documentsOnly}
						onClick={() =>
							setFilter((prev) => ({
								...prev,
								upcomingType: option,
								upcomingDocumentsOnly: false,
							}))
						}
						isLast={!hasDocumentsFeature && i === TYPE_OPTIONS.length - 1}
						testId={`notification-center-filter-type-${option}`}
					/>
				))}
				{hasDocumentsFeature ? (
					<DocumentsFilterRow
						selected={documentsOnly}
						onClick={() =>
							setFilter((prev) => ({
								...prev,
								upcomingType: UpcomingTimelineItemTypeFilterEnum.All,
								upcomingDocumentsOnly: true,
							}))
						}
					/>
				) : null}
			</FilterSection>
		</Stack>
	);
};
